// Pictures for the words that can carry one.
//
// The companion to the character hints, and deliberately the smaller half. A picture is the best
// mnemonic there is for a concrete thing — nobody forgets what 猫 means once they have seen a cat
// beside it — and it is useless for everything else. Of the 242 nouns and verbs in the Top 500,
// most are abstract: 可能, 应该, 情况, 成为, 问题. You cannot draw "might happen" without inventing
// a private code the learner has to learn first, which is a worse deal than no picture at all.
//
// So this covers the words that ARE things and actions, and the card shows nothing for the rest
// rather than reaching for something vague. The character hints carry the abstract words instead.
//
// Drawn on the same 120×92 stage and in the same flat style as the home page, so a picture here
// looks like it belongs to the same app. Colour is `currentColor` plus washes of it, set by the
// card, so all 44 themes work without any of this naming a colour.

const C = 'currentColor';
const WASH = 'color-mix(in srgb, currentColor 26%, transparent)';
const SOFT = 'color-mix(in srgb, currentColor 14%, transparent)';

/** The same person as the home page, so the two sets read as one hand. */
function figure(x: number, y: number, scale = 1, fill = C, lean = 0): string {
  return (
    `<g transform="translate(${x} ${y}) scale(${scale}) rotate(${lean})">` +
    `<circle cx="0" cy="-13" r="6.4" fill="${fill}"/>` +
    `<path d="M-8.4 8c0-6.2 3.4-9.6 8.4-9.6s8.4 3.4 8.4 9.6z" fill="${fill}"/>` +
    `</g>`
  );
}

const ground = `<ellipse cx="60" cy="82" rx="38" ry="5.5" fill="${SOFT}"/>`;

/**
 * The pictures, keyed by WHAT THEY SHOW rather than by how one language spells it.
 *
 * This was keyed by the Chinese word -- ART['猫'], ART['狗'] -- and looked up with the learned
 * word's surface form, so a picture was found only where a course happened to spell the concept
 * the way Chinese does. Measured on the shipped vocabularies: zh-en reached all 31, ja-en reached
 * 9 by coincidence of shared kanji, and the two courses that teach English reached none at all.
 * 見る never found the picture drawn for 看; 犬 never found the one drawn for 狗 (MYN-173).
 *
 * A picture is of a THING, and the name here is that thing in English because this table is
 * written in English. Which of a course's words each picture belongs to is the course's own
 * business, and is declared in pair.pictures.
 */
const ART: Record<string, string> = {
  // --- people and the body
  person: ground + figure(60, 76, 1.5),
  child: ground + figure(44, 78, 1.15) + figure(78, 80, 0.8),
  friend: ground + figure(44, 78, 1.05, C, -5) + figure(78, 78, 1.05, WASH, 5) +
    `<path d="M52 62c5-4 11-4 16 0" stroke="${C}" stroke-width="3" fill="none" stroke-linecap="round"/>`,
  hand: `<path d="M44 80V44c0-4 6-4 6 0v14V30c0-4 6-4 6 0v26V26c0-4 6-4 6 0v30V34c0-4 6-4 6 0v30c0 10-6 16-15 16z" fill="${WASH}" stroke="${C}" stroke-width="2.6" stroke-linejoin="round"/>`,
  heart: `<path d="M60 80S26 60 26 42a17 17 0 0 1 34-6 17 17 0 0 1 34 6c0 18-34 38-34 38z" fill="${WASH}" stroke="${C}" stroke-width="3" stroke-linejoin="round"/>`,
  eye: `<path d="M14 46c16-18 76-18 92 0-16 18-76 18-92 0z" fill="${SOFT}" stroke="${C}" stroke-width="3" stroke-linejoin="round"/>` +
    `<circle cx="60" cy="46" r="13" fill="${WASH}" stroke="${C}" stroke-width="2.6"/><circle cx="60" cy="46" r="5" fill="${C}"/>`,

  // --- seeing, hearing, speaking
  see: `<path d="M14 46c16-18 76-18 92 0-16 18-76 18-92 0z" fill="${SOFT}" stroke="${C}" stroke-width="3" stroke-linejoin="round"/>` +
    `<circle cx="60" cy="46" r="12" fill="${WASH}" stroke="${C}" stroke-width="2.6"/><circle cx="60" cy="46" r="4.6" fill="${C}"/>` +
    `<path d="M30 70h60" stroke="${C}" stroke-width="3" stroke-linecap="round" opacity="0.4"/>`,
  listen: `<path d="M74 20a26 26 0 0 0-26 26v22c0 6-6 8-6 12h20" fill="${WASH}" stroke="${C}" stroke-width="3" stroke-linejoin="round"/>` +
    `<path d="M20 34a22 22 0 0 0 0 28M8 26a34 34 0 0 0 0 44" fill="none" stroke="${C}" stroke-width="3.2" stroke-linecap="round" opacity="0.7"/>`,
  say: ground + figure(44, 78, 1.05) +
    `<path d="M62 20h48c3 0 5 2 5 5v18c0 3-2 5-5 5H80l-9 7v-7h-9c-3 0-5-2-5-5V25c0-3 2-5 5-5z" fill="${WASH}" stroke="${C}" stroke-width="2.6" stroke-linejoin="round"/>` +
    `<path d="M70 30h30M70 38h18" stroke="${C}" stroke-width="2.6" stroke-linecap="round" opacity="0.7"/>`,
  ask: `<path d="M18 18h84c4 0 6 3 6 6v34c0 4-2 6-6 6H52l-14 11V64H18c-4 0-6-2-6-6V24c0-3 2-6 6-6z" fill="${WASH}" stroke="${C}" stroke-width="3" stroke-linejoin="round"/>` +
    `<path d="M50 33a10 10 0 1 1 11 10v4" fill="none" stroke="${C}" stroke-width="4" stroke-linecap="round"/><circle cx="61" cy="54" r="3.2" fill="${C}"/>`,

  // --- going and moving
  walk: ground + `<g transform="translate(58 74)"><circle cx="0" cy="-30" r="7" fill="${C}"/>` +
    `<path d="M-2-22l2 14M0-8l-10 12M0-8l11 10M-2-18l-12 6M-2-18l13 4" stroke="${C}" stroke-width="4.2" fill="none" stroke-linecap="round"/></g>`,
  run: ground + `<g transform="translate(58 74) rotate(-8)"><circle cx="0" cy="-30" r="7" fill="${C}"/>` +
    `<path d="M-2-22l4 13M2-9l-14 9M2-9l13 13M-2-19l-14 2M-2-19l15-3" stroke="${C}" stroke-width="4.2" fill="none" stroke-linecap="round"/></g>`,
  come: `<path d="M20 46h58" stroke="${C}" stroke-width="5" stroke-linecap="round"/>` +
    `<path d="M60 26l22 20-22 20z" fill="${C}"/>` + ground + figure(96, 78, 0.8, WASH),
  go: `<path d="M100 46H42" stroke="${C}" stroke-width="5" stroke-linecap="round"/>` +
    `<path d="M60 26L38 46l22 20z" fill="${C}"/>` + ground + figure(24, 78, 0.8, WASH),
  car: `<path d="M14 62V46l12-16h46l16 16h18v16z" fill="${WASH}" stroke="${C}" stroke-width="3" stroke-linejoin="round"/>` +
    `<path d="M34 32v14h22V32" fill="none" stroke="${C}" stroke-width="2.4" opacity="0.7"/>` +
    `<circle cx="36" cy="64" r="9" fill="${C}"/><circle cx="86" cy="64" r="9" fill="${C}"/>`,

  // --- eating, drinking, buying
  eat: ground + `<path d="M34 26v24c0 5 4 8 8 8v22M42 26v18M50 26v18" stroke="${C}" stroke-width="3.4" fill="none" stroke-linecap="round"/>` +
    `<path d="M78 26c7 0 11 7 11 16s-4 14-8 14v24" fill="none" stroke="${C}" stroke-width="3.4" stroke-linecap="round"/>`,
  meal: `<path d="M24 50h72a36 36 0 0 1-72 0z" fill="${WASH}" stroke="${C}" stroke-width="3" stroke-linejoin="round"/>` +
    `<path d="M16 80h88" stroke="${C}" stroke-width="3.4" stroke-linecap="round"/>` +
    `<path d="M44 40c-2-6 0-10 4-13M60 38c-2-7 0-11 4-14M76 40c-2-6 0-10 4-13" stroke="${C}" stroke-width="2.6" fill="none" stroke-linecap="round" opacity="0.7"/>`,
  water: `<path d="M60 14s26 28 26 44a26 26 0 1 1-52 0c0-16 26-44 26-44z" fill="${WASH}" stroke="${C}" stroke-width="3" stroke-linejoin="round"/>` +
    `<path d="M48 58a12 12 0 0 0 6 14" fill="none" stroke="${C}" stroke-width="2.6" stroke-linecap="round" opacity="0.6"/>`,
  money: `<circle cx="46" cy="52" r="24" fill="${SOFT}" stroke="${C}" stroke-width="3"/>` +
    `<circle cx="76" cy="46" r="24" fill="${WASH}" stroke="${C}" stroke-width="3"/>` +
    `<path d="M76 34v24M68 42h16M68 50h16" stroke="${C}" stroke-width="3" stroke-linecap="round"/>`,
  buy: `<path d="M22 34h76l-8 44H30z" fill="${WASH}" stroke="${C}" stroke-width="3" stroke-linejoin="round"/>` +
    `<path d="M44 34V24a16 16 0 0 1 32 0v10" fill="none" stroke="${C}" stroke-width="3" stroke-linecap="round"/>`,

  // --- places and time
  home: `<path d="M12 78V44L60 14l48 30v34z" fill="${WASH}" stroke="${C}" stroke-width="3" stroke-linejoin="round"/>` +
    `<path d="M6 46L60 12l54 34" fill="none" stroke="${C}" stroke-width="3.4" stroke-linejoin="round" stroke-linecap="round"/>` +
    `<rect x="48" y="54" width="24" height="24" rx="2" fill="${C}" opacity="0.55"/>`,
  school: `<path d="M14 78V42h92v36z" fill="${WASH}" stroke="${C}" stroke-width="3" stroke-linejoin="round"/>` +
    `<path d="M8 42L60 18l52 24" fill="none" stroke="${C}" stroke-width="3.2" stroke-linejoin="round" stroke-linecap="round"/>` +
    `<path d="M60 18V6M60 6h14v8H60" fill="${C}" stroke="${C}" stroke-width="2.4" stroke-linejoin="round"/>` +
    `<rect x="48" y="56" width="24" height="22" rx="2" fill="${C}" opacity="0.5"/>`,
  moon: `<path d="M84 20a32 32 0 1 0 0 56 38 38 0 0 1 0-56z" fill="${WASH}" stroke="${C}" stroke-width="3" stroke-linejoin="round"/>` +
    `<circle cx="26" cy="22" r="2.6" fill="${C}" opacity="0.6"/><circle cx="42" cy="34" r="1.8" fill="${C}" opacity="0.45"/>`,
  book: `<path d="M60 30C52 22 40 18 22 18v48c18 0 30 4 38 12 8-8 20-12 38-12V18c-18 0-30 4-38 12z" fill="${WASH}" stroke="${C}" stroke-width="3" stroke-linejoin="round"/>` +
    `<path d="M60 30v48" stroke="${C}" stroke-width="2.6"/>`,

  // --- holding and putting
  take: `<path d="M30 78V48c0-4 6-4 6 0v8V34c0-4 6-4 6 0v20V30c0-4 6-4 6 0v24V36c0-4 6-4 6 0v42z" fill="${WASH}" stroke="${C}" stroke-width="2.6" stroke-linejoin="round"/>` +
    `<rect x="62" y="28" width="34" height="26" rx="4" fill="${C}" opacity="0.6"/>`,
  put: `<rect x="36" y="18" width="34" height="26" rx="4" fill="${WASH}" stroke="${C}" stroke-width="2.6"/>` +
    `<path d="M53 50v14" stroke="${C}" stroke-width="3" stroke-linecap="round" stroke-dasharray="4 5"/>` +
    `<path d="M18 78h84" stroke="${C}" stroke-width="3.6" stroke-linecap="round"/>`,
  open: `<path d="M64 20h34v56H64z" fill="${SOFT}" stroke="${C}" stroke-width="3" stroke-linejoin="round"/>` +
    `<path d="M58 14L26 26v50l32 10z" fill="${WASH}" stroke="${C}" stroke-width="3" stroke-linejoin="round"/>` +
    `<circle cx="50" cy="52" r="3.4" fill="${C}"/>`,

  // --- weather and nature
  rain: `<path d="M38 44a16 16 0 0 1 1-32 21 21 0 0 1 40 7 13 13 0 0 1-2 25z" fill="${WASH}" stroke="${C}" stroke-width="3" stroke-linejoin="round"/>` +
    `<path d="M40 56l-5 16M58 56l-5 16M76 56l-5 16" stroke="${C}" stroke-width="3.6" stroke-linecap="round"/>`,
  sky: `<circle cx="60" cy="42" r="19" fill="${WASH}" stroke="${C}" stroke-width="3"/>` +
    `<path d="M60 12v8M60 64v8M30 42h-8M98 42h-8M39 21l-6-6M87 69l-6-6M81 21l6-6M33 69l6-6" stroke="${C}" stroke-width="3.2" stroke-linecap="round"/>`,
  cat: ground +
    `<path d="M36 44V24l12 9M84 44V24l-12 9" fill="${WASH}" stroke="${C}" stroke-width="3" stroke-linejoin="round"/>` +
    `<path d="M36 44a24 24 0 0 0 48 0" fill="${WASH}" stroke="${C}" stroke-width="3"/>` +
    `<circle cx="50" cy="48" r="3" fill="${C}"/><circle cx="70" cy="48" r="3" fill="${C}"/>` +
    `<path d="M60 56v4M24 52h12M84 52h12" stroke="${C}" stroke-width="2.6" stroke-linecap="round"/>`,
  dog: ground +
    `<path d="M34 34l-6-16 16 7M86 34l6-16-16 7" fill="${WASH}" stroke="${C}" stroke-width="3" stroke-linejoin="round"/>` +
    `<ellipse cx="60" cy="48" rx="26" ry="22" fill="${WASH}" stroke="${C}" stroke-width="3"/>` +
    `<circle cx="50" cy="44" r="3" fill="${C}"/><circle cx="70" cy="44" r="3" fill="${C}"/>` +
    `<ellipse cx="60" cy="58" rx="6" ry="4.4" fill="${C}"/>`,
};

/** The stage every scene is authored on, so the figures can share coordinates. */
const STAGE_W = 120;
const STAGE_H = 92;

/** Enough slack for the widest stroke, which getBBox does not count. */
const PAD = 3;

let host: HTMLElement | null = null;

/** Somewhere off-screen but laid out, because getBBox on an unrendered element measures nothing. */
function measureHost(): HTMLElement {
  if (!host) {
    host = document.createElement('div');
    host.style.cssText = 'position:absolute;left:-9999px;top:0;width:132px;visibility:hidden';
    document.body.append(host);
  }
  return host;
}

/**
 * Crop the viewBox to what the scene actually draws.
 *
 * The stage is shared so that a figure standing on the ground line is at the same coordinates in
 * every picture, but most scenes use only part of it — 人 is one person on that line, and the
 * whole top half of the box is empty. Shipped as-is that emptiness does not read as part of the
 * drawing; it reads as a gap between the word's meaning and its picture.
 *
 * VERTICALLY ONLY, and this is the part worth keeping. Cropping both axes would fit each drawing
 * snugly to its own box, and since the box is a fixed width on the page, a small drawing would be
 * silently blown up to the size of a large one — 心 the size of 学校. Holding the width at the full
 * stage keeps one stage unit the same number of pixels in every picture, so the pictures stay a set
 * rather than a series of unrelated sizes; only the height follows the content.
 */
function trimToContent(svg: SVGSVGElement): void {
  const stage = measureHost();
  stage.append(svg);
  let box: DOMRect | null = null;
  try {
    box = svg.getBBox();
  } catch {
    box = null;
  }
  svg.remove();
  // No measurement is a reason to leave the stage alone, not to guess at one.
  if (!box || box.height <= 0) return;
  const top = Math.max(0, Math.floor(box.y - PAD));
  const bottom = Math.min(STAGE_H, Math.ceil(box.y + box.height + PAD));
  svg.setAttribute('viewBox', `0 ${top} ${STAGE_W} ${bottom - top}`);
}

/** The picture for a word, or null when it is not one a picture can carry. */
export function wordArt(picture: string | undefined): SVGSVGElement | null {
  const art = picture ? ART[picture] : undefined;
  if (!art) return null;
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', `0 0 ${STAGE_W} ${STAGE_H}`);
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('class', 'word-art');
  // Authored in this file, never from data — no untrusted content reaches innerHTML.
  svg.innerHTML = art;
  trimToContent(svg);
  return svg;
}

/** How many words have one, for the build report and the tests. */
export const DRAWN_WORDS = Object.keys(ART);
